package com.islamicmedia.site.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private data class Founder(val name: String, val titlesAnalyzed: Int)

// Placeholder founding members + their (placeholder) "titles analyzed" stat — swap for real data.
private val FOUNDING_MEMBERS = listOf(
    Founder("Ahmad Khan", 42), Founder("Sara Malik", 28), Founder("Yusuf Rahman", 51),
    Founder("Fatima Noor", 19), Founder("Bilal Aziz", 33), Founder("Maryam Siddiqui", 47),
    Founder("Omar Farooq", 24), Founder("Khadija Yusuf", 38), Founder("Ibrahim Ali", 16),
    Founder("Zainab Hassan", 61), Founder("Hamza Sheikh", 29), Founder("Aisha Kareem", 44),
    Founder("Musa Abdullah", 22), Founder("Layla Iqbal", 35), Founder("Tariq Mahmood", 53),
    Founder("Sumaya Rashid", 27), Founder("Yahya Osman", 18), Founder("Noor Jahan", 40),
    Founder("Idris Salim", 31), Founder("Ruqayya Amin", 49),
)

/**
 * A slim black ticker at the very top of the page showcasing founding members: white names
 * separated by gold stars, scrolling horizontally in a seamless loop. Two identical passes with a
 * 0 → -50% translate keep the loop seamless; pauses on hover, honours `prefers-reduced-motion`.
 *
 * Hovering (or tapping, on touch) a name drops a black "dossier tab" DOWN out of the bar revealing
 * that member's "N titles analyzed" stat — the tab slides open with a smooth decelerating ease so
 * it reads like a physical tab being pulled down. Tap works via `:focus-within` (each name is
 * focusable), which also pauses the scroll so the tab stays put.
 */
@Composable
fun FoundingMembersBar() {
    InjectFoundingMembersStyles()

    Div(attrs = { classes("founders-bar") }) {
        Div(attrs = { classes("founders-track") }) {
            // Two identical passes so translateX(-50%) loops seamlessly.
            repeat(2) {
                FOUNDING_MEMBERS.forEach { f ->
                    Span(attrs = { classes("founder-star") }) { Text("★") }
                    Div(attrs = {
                        classes("founder-item")
                        attr("tabindex", "0") // focusable → tap on mobile triggers :focus-within
                    }) {
                        // name + joined scale up together on hover/tap (label wrapper so the drawer
                        // sibling is unaffected)
                        Div(attrs = { classes("founder-label") }) {
                            Span(attrs = { classes("founder-name") }) { Text(f.name) }
                            Span(attrs = { classes("founder-joined") }) { Text("joined April 2026") }
                        }
                        Div(attrs = { classes("founder-drawer") }) {
                            Text("${f.titlesAnalyzed} titles analyzed")
                        }
                    }
                }
            }
        }
    }
}

/**
 * Injects the ticker's CSS exactly once via a real <style> element (the browser's full CSS engine),
 * rather than Compose HTML's `Style {}` builder whose naive parser drops blocks with nested @media.
 */
@Composable
private fun InjectFoundingMembersStyles() {
    LaunchedEffect(Unit) {
        val styleId = "founders-bar-styles"
        val doc = kotlinx.browser.document
        if (doc.getElementById(styleId) != null) return@LaunchedEffect
        val style = doc.createElement("style") as org.w3c.dom.HTMLStyleElement
        style.id = styleId
        style.textContent = """
            .founders-bar {
                width: 100%;
                background: #000;
                /* clip the marquee horizontally, but let a hovered name's dossier tab spill DOWN */
                overflow-x: clip;
                overflow-y: visible;
                padding: 7px 0;
                position: relative;
                z-index: 30; /* lift the bar + its drop-down tab above the hero below */
            }
            .founders-track {
                display: flex;
                align-items: center;
                width: max-content;
                animation: founders-scroll 67.5s linear infinite;
                will-change: transform;
            }
            /* pause on hover (desktop) OR when a name is focused/tapped (mobile) */
            .founders-bar:hover .founders-track,
            .founders-bar:focus-within .founders-track { animation-play-state: paused; }
            @keyframes founders-scroll {
                from { transform: translateX(0); }
                to   { transform: translateX(-50%); }
            }
            @media (prefers-reduced-motion: reduce) {
                .founders-track { animation: none; }
            }
            .founder-item {
                display: flex;
                flex-direction: column;
                align-items: center;
                line-height: 1.15;
                position: relative;   /* anchor for the absolute drawer */
                padding: 0 4px;
                cursor: pointer;
                outline: none;        /* state is shown by the tab, not a focus ring */
            }
            .founder-item:hover, .founder-item:focus-within { z-index: 2; }
            /* name + joined scale up ~25% together on hover/tap (transform → no marquee reflow) */
            .founder-label {
                display: flex;
                flex-direction: column;
                align-items: center;
                transform-origin: center top;
                transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
            }
            .founder-item:hover .founder-label,
            .founder-item:focus-within .founder-label {
                transform: scale(1.25);
            }
            .founder-name {
                color: #fff;
                font-size: 13px;
                font-weight: 600;
                letter-spacing: 0.6px;
                white-space: nowrap;
            }
            .founder-joined {
                color: rgba(255,255,255,0.55);
                font-size: 10px;
                font-style: italic;
                font-weight: 400;
                letter-spacing: 0.3px;
                white-space: nowrap;
                margin-top: 1px;
            }
            .founder-star {
                color: #C8962E;
                font-size: 12px;
                margin: 0 18px;
                flex: 0 0 auto;
                transform-origin: center;
                transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
            }
            /* the two stars flanking a hovered/focused name expand along with it
               (+ .founder-star = the star AFTER it; :has(+ ...) = the star BEFORE it) */
            .founder-item:hover + .founder-star,
            .founder-item:focus-within + .founder-star,
            .founder-star:has(+ .founder-item:hover),
            .founder-star:has(+ .founder-item:focus-within) {
                transform: scale(1.25);
            }
            /* the dossier tab that flows down out of the black bar on hover/tap */
            .founder-drawer {
                position: absolute;
                top: calc(100% + 6px);     /* overlap ~1px into the bar's black so the join is seamless */
                left: 50%;
                transform: translateX(-50%);
                background: #000;
                color: #FACC15;            /* gold stat to pop on black */
                border-radius: 0 0 12px 12px;   /* convex rounded bottom corners */
                box-shadow: 0 10px 22px rgba(0,0,0,0.5);
                white-space: nowrap;
                font-size: 11px;
                font-weight: 500;
                letter-spacing: 0.4px;
                max-height: 0;
                padding: 0 16px;
                opacity: 0;
                /* clip vertically for the drop animation, but let the concave side fillets show */
                overflow-x: visible;
                overflow-y: clip;
                /* decelerating ease → reads like a tab pulled down and settling into place */
                transition: max-height 0.42s cubic-bezier(0.16, 1, 0.3, 1),
                            padding   0.42s cubic-bezier(0.16, 1, 0.3, 1),
                            opacity   0.28s ease;
            }
            /* concave fillets: blend the tab's TOP corners smoothly into the black bar (no 90° notch).
               Each is a small square beside the top corner, black except a transparent quarter-circle,
               so the black flares outward into the bar and the hero shows through the curve. */
            .founder-drawer::before, .founder-drawer::after {
                content: "";
                position: absolute;
                top: 0;
                width: 14px;
                height: 14px;
            }
            .founder-drawer::before {
                left: -14px;
                background: radial-gradient(circle at bottom left, transparent 14px, #000 14.5px);
            }
            .founder-drawer::after {
                right: -14px;
                background: radial-gradient(circle at bottom right, transparent 14px, #000 14.5px);
            }
            .founder-item:hover .founder-drawer,
            .founder-item:focus-within .founder-drawer {
                max-height: 44px;
                padding: 8px 16px 9px;
                opacity: 1;
            }
        """.trimIndent()
        doc.head?.appendChild(style)
    }
}
